<template>
  <div class="layout">
    <!-- Header -->    
    <header class="header">
      <div class="container">
        <div class="logo">
          <router-link to="/">
            <img :src="logo?.coverImages ? `${fileUrl}${logo.coverImages}` : defaultLogo" alt="公司logo">
          </router-link>
        </div>
        <nav class="nav">
          <router-link 
            v-for="item in navItems" 
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{ active: $route.path === item.path }"
          >
            {{ item.name }}
          </router-link>
        </nav>
        <div class="header-actions">
          <router-link to="/cart" class="cart-action-btn" :class="{
            'cart-has-items': isLoggedIn && cartStore.totalCount > 0,
            'cart-empty': !isLoggedIn || cartStore.totalCount === 0
          }">
            <span class="cart-text">购物车</span>
            <span class="cart-icon-wrapper">
              <span class="cart-icon">
                <i class="el-icon-shopping-cart-2"></i>
              </span>
            </span>
          </router-link>
          <template v-if="!isLoggedIn">
            <button class="auth-btn" @click="showLogin = true">登录/注册</button>
          </template>
          <template v-else>
            <el-dropdown @command="handleCommand" trigger="click">
              <div class="user-avatar">
                <img :src="userInfo?.avatar || defaultAvatar" alt="用户头像">
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                  <el-dropdown-item command="address">收货地址管理</el-dropdown-item>
                  <el-dropdown-item command="orders">我的订单</el-dropdown-item>
                  <el-dropdown-item command="logout">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            
          </template>
          
        </div>
        
      </div>
    </header>

    <!-- Main Content -->
    <main class="main">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <!-- Footer -->
    <footer class="footer" v-if="showFooter">
      <div class="container">
        <div class="footer-content">          
          <div class="footer-info">
            <h3>联系我们</h3>
            <template v-if="contactItems.length">
              <p v-for="(item, index) in contactItems" :key="index">
                {{ item.label }}：{{ item.description }}
              </p>
            </template>
          </div>
          <div class="footer-links">
            <h3>快速链接</h3>
            <router-link to="/about">关于我们</router-link>
            <router-link to="/products">产品中心</router-link>
            <router-link to="/news">新闻动态</router-link>
            <router-link to="/contact">联系我们</router-link>
          </div>
        </div>
        <div class="footer-bottom">
          <p>
            &copy; 2025 佐边电子科技有限公司. 保留所有权利
            <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=粤公网安备44060502003980号" target="_blank" class="beian-link">粤公网安备44060502003980号</a>
          </p>
        </div>
      </div>
    </footer>

    <!-- Contact Float -->
    <ContactFloat />
    <Login v-model="showLogin" />
    <UserProfileDialog v-model:showProfile="showProfileDialog" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import ContactFloat from '@/components/ContactFloat/index.vue'
import Login from '@/components/Auth/index.vue'
import UserProfileDialog from '@/components/UserProfileDialog.vue'
import { getContactUs, getLogo } from '@/api'
import { AboutUs } from '@/types/aboutUs'
import defaultLogo from '@/assets/logo.png'
import defaultAvatar from '@/assets/default-avatar.png'
import { ElMessage } from 'element-plus'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { useLayoutStore } from '@/stores/layout'

const router = useRouter()
const route = useRoute()
const fileUrl = import.meta.env.VITE_APP_FILE_URL
const logo = ref<AboutUs>()
const contactItems = ref<AboutUs[]>([])
const showLogin = ref(false)
const showProfileDialog = ref(false)

const cartStore = useCartStore()
const authStore = useUserStore()
const layoutStore = useLayoutStore()
const { userInfo, isLoggedIn    } = storeToRefs(authStore)
const { showFooter } = storeToRefs(layoutStore)

// watch route query for login dialog
watch(() => route.query.showLogin, (newVal) => {
  if (newVal === '1') {
    showLogin.value = true
  } else {
    showLogin.value = false
  }
}, { immediate: true })

// 处理用户下拉菜单命令
const handleCommand = async (command: string) => {
  switch (command) {
    case 'profile':
      showProfileDialog.value = true
      break
    case 'orders':
      router.push('/user/orders')
      break
    case 'address':
      router.push('/address')
      break
    case 'logout':
      try {
        authStore.logout()
        ElMessage.success('退出成功')
        router.push('/')
      } catch (error) {
        console.error('退出失败:', error)
      }
      break
  }
}

onMounted(async () => {
  try {
    const response = await getContactUs()
    if (response && response.data) {
      contactItems.value = response.data
    }
    const logoResponse = await getLogo()
    if (logoResponse && logoResponse.data) {
      logo.value = logoResponse.data
    }
  } catch (error) {
    console.error('Error fetching data:', error)
  }
})

const navItems = ref([
  { path: '/', name: '首页' },
  { path: '/products', name: '产品中心' },
  { path: '/news', name: '新闻动态' },
  { path: '/cases', name: '成功案例' },
  { path: '/about', name: '关于我们' },
  { path: '/contact', name: '联系我们' }
])
</script>


<style scoped>
.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  background: linear-gradient(90deg, rgba(14, 17, 17, 0.95) 0%, rgba(255, 255, 255, 0.2) 100%);
  /* box-shadow: 0 4px 24px rgba(36,198,220,0.15), 0 1.5px 0 #47869b inset; */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  backdrop-filter: blur(8px);
  border-bottom: 1.5px solid rgba(36,198,220,0.25);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.header .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

.logo {
  height: 44px;              /* 适中高度 */
  max-width: 180px;          /* 防止过大 */
  display: flex;
  align-items: center;
  color: #fff !important;    /* 白色字体/图标 */
  font-size: 2rem;           /* 适中字号 */
  font-weight: bold;
  text-shadow: 0 2px 8px rgba(0,0,0,0.5); /* 阴影提升可读性 */
  margin-right: 32px;
  user-select: none;
}

.logo img {
  height: 48px;
  width: auto;
  filter: drop-shadow(0 2px 12px #eef1f1) drop-shadow(0 0 8px #fff);
  background: rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 4px 12px;
}

.nav {
  display: flex;
  gap: 2.5rem;
  flex-wrap: nowrap; /* 防止子项换行 */
}

.nav-item {
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1.2rem;
  position: relative;
  letter-spacing: 1px;
  transition: color 0.3s, background 0.3s;
  border-radius: 6px;
  overflow: hidden;
  white-space: nowrap; /* 防止文字换行 */
}
.nav-item::before {
  content: '';
  position: absolute;
  left: 50%; top: 50%;
  width: 0; height: 0;
  background: rgba(36,198,220,0.18);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
  z-index: 0;
}
.nav-item:hover::before {
  width: 180%;
  height: 300%;
}
.nav-item:hover, .nav-item.active {
  color: #24c6dc;
  font-weight: 500;
  border-bottom: 2px solid #24c6dc;
}
.nav-item span {
  position: relative;
  z-index: 1;
}

.nav-item.active::after {
  content: '';
  display: block;
  margin: 0 auto;
  width: 60%;
  height: 2px;
  background: #dce6e7;
  border-radius: 2px;
  margin-top: 6px;
  box-shadow: 0 0 8px #24c6dc;
}

.main {
  flex: 1;
  margin-top: 80px;
}

.footer {
  background: 
    linear-gradient(0deg, rgba(14, 17, 17, 0.92) 0%, rgba(14, 17, 17, 0.75) 50%, rgba(14, 17, 17, 0.82) 100%),
    linear-gradient(90deg, rgba(36, 198, 220, 0.05) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(36, 198, 220, 0.05) 100%);
  color: white;
  padding: 4rem 0 2rem;
  box-shadow: 0 -4px 24px rgba(36,198,220,0.15);
  position: relative;
  backdrop-filter: blur(8px);
  border-top: 1.5px solid rgba(36,198,220,0.25);
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
}

.footer-info h3,
.footer-links h3 {
  margin-bottom: 1.2rem;
  font-size: 1.3rem;
  color: #24c6dc;
  text-shadow: 0 0 12px rgba(36, 198, 220, 0.3);
  font-weight: 600;
  letter-spacing: 0.5px;
} 

.footer-info p {
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.3s;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s;
  position: relative;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 4px;
}

.footer-links a:hover {
  color: #24c6dc;
  background: rgba(36, 198, 220, 0.1);
  text-shadow: 0 0 8px rgba(36, 198, 220, 0.5);
}

.footer-bottom {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(36, 198, 220, 0.15);
  color: rgba(255, 255, 255, 0.7);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: 2rem;
}

.action-btn {
  position: relative;
  color: #fff;
  font-size: 1.5rem;
  padding: 0.5rem;
  transition: color 0.3s;
}

.cart-count {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #24c6dc;
  color: white;
  font-size: 0.8rem;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.auth-btn {
  color: #24c6dc;
  padding: 0.5rem 1.2rem;
  border: 1.5px solid rgba(255,255,255,0.6);
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s;
  background: #f7f7f7;
  white-space: nowrap;
  height: 44px;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  box-sizing: border-box;
}
.auth-btn:hover {
  background: rgba(255,255,255,0.1);
  border-color: #24c6dc;
}

.user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid rgba(255,255,255,0.3);
  transition: border-color 0.3s;
  background: #f5f5f5;
}
.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  background: #f5f5f5; /* 可选，给空白区域加个背景色 */
  margin-top: 6px;
}
.user-avatar:hover {
  border-color: #24c6dc;
}


.cart-action-btn {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  border: none;
  background: #fff;
  border-radius: 20px;
  padding: 4px 18px 4px 12px;
  box-shadow: 0 2px 8px rgba(230,0,18,0.08);
  transition: box-shadow 0.2s, background 0.2s, color 0.2s;
  position: relative;
  margin-left: 24px;
  color: #bbb;
  white-space: nowrap; /* 防止文字换行 */
  height: 44px;
  min-width: 80px;
}
.cart-action-btn.cart-has-items {
  color: #ff6b6b;
}
.cart-action-btn.cart-empty {
  color: #bbb;
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.7;
}
.cart-text {
  margin-right: 6px;
  font-weight: bold;
  white-space: nowrap; /* 防止文字换行 */
}
.cart-icon-wrapper {
  display: flex;
  align-items: center;
}
.cart-icon {
  position: relative;
  display: inline-block;
}
.cart-icon-wrapper i {
  font-size: 22px;
}
.cart-badge {
  position: absolute;
  top: -14px;
  right: -12px;
  color: #e60012;
  background: none;
  border: none;
  border-radius: 0;
  font-size: 12px;
  font-weight: bold;
  min-width: unset;
  height: unset;
  padding: 0;
  box-shadow: none;
  line-height: 1;
  z-index: 1;
  display: inline;
}

.beian-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  margin-left: 15px;
  font-size: 12px;
  transition: color 0.3s ease;
}

.beian-link:hover {
  color: #fff;
}

@media (max-width: 768px) {
  .header .container {
    height: 60px;
  }
  .logo img {
    height: 32px;
    padding: 2px 6px;
  }
  .nav {
    gap: 1rem;
  }
  .main {
    margin-top: 60px;
  }
  .footer {
    padding: 2rem 0 1rem;
  }
}

.navbar {
  background: rgba(30, 30, 30, 0.92); /* 更深色，提升对比度 */
  height: 64px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 0 40px;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(6px);
}

.navbar .nav-menu {
  display: flex;
  gap: 32px;
  align-items: center;
}
.navbar .nav-menu a {
  color: #fff;
  font-size: 1.1rem;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background 0.2s, color 0.2s;
}
.navbar .nav-menu a:hover,
.navbar .nav-menu .router-link-active {
  background: rgba(255,255,255,0.12);
  color: #ffd700;
}
</style>